import { onIdTokenChanged, signInWithCustomToken, signOut, type User } from "firebase/auth";
import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { firebaseDienste } from "./firebase";

/** Sitzung der App (identitaeten.md §1, §2): Konto (Passkey) oder Link-Zugang (`link-<TeilnehmerId>`). */
export interface Sitzung {
  status: "laedt" | "ohne" | "angemeldet";
  uid: string | null;
  art: "passkey" | "link" | null;
}

const Kontext = createContext<Sitzung>({ status: "laedt", uid: null, art: null });

/**
 * Custom Token einer gerade abgeschlossenen Zeremonie, bis Firebase die Sitzung festgehalten hat. Wird die App in dieser
 * Zeit neu geladen (Netzwechsel, Neustart, Wechsel der Ansicht), meldet sie sich beim Start damit an, statt die
 * abgeschlossene Zeremonie zu verlieren; das Token gilt höchstens eine Stunde und bleibt nur in diesem Tab.
 */
const AUSSTEHEND = "dj-anmeldung-ausstehend";

function ausstehendesToken(): string | null {
  try { return sessionStorage.getItem(AUSSTEHEND); } catch { return null; }
}

/**
 * Abmeldung, bis Firebase sie festgehalten hat: Wird die App vorher neu geladen, meldet sie sich beim Start erneut ab,
 * statt mit der alten Sitzung weiterzuarbeiten (Gegenstück zur ausstehenden Anmeldung).
 */
const ABMELDUNG = "dj-abmeldung-ausstehend";

function abmeldungAussteht(): boolean {
  try { return sessionStorage.getItem(ABMELDUNG) === "1"; } catch { return false; }
}

function abmeldungSetzen(aussteht: boolean): void {
  try {
    if (aussteht) sessionStorage.setItem(ABMELDUNG, "1");
    else sessionStorage.removeItem(ABMELDUNG);
  } catch { /* ohne sessionStorage keine Wiederaufnahme */ }
}

function ausstehendSetzen(token: string | null): void {
  try {
    if (token) sessionStorage.setItem(AUSSTEHEND, token);
    else sessionStorage.removeItem(AUSSTEHEND);
  } catch { /* ohne sessionStorage keine Wiederaufnahme */ }
}

function aus(user: User | null): Sitzung {
  if (!user) return { status: "ohne", uid: null, art: null };
  return { status: "angemeldet", uid: user.uid, art: user.uid.startsWith("link-") ? "link" : "passkey" };
}

export function SitzungAnbieter({ children }: { children: ReactNode }) {
  const { auth } = firebaseDienste();
  const [sitzung, setSitzung] = useState<Sitzung>(() => (auth.currentUser ? aus(auth.currentUser) : { status: "laedt", uid: null, art: null }));
  useEffect(() => onIdTokenChanged(auth, (u) => {
    if (u && abmeldungAussteht()) {
      void signOut(auth);
      return;
    }
    if (!u) abmeldungSetzen(false);
    const ausstehend = ausstehendesToken();
    if (!u && ausstehend) {
      void mitCustomTokenAnmelden(ausstehend).catch(() => { ausstehendSetzen(null); setSitzung(aus(null)); });
      return;
    }
    setSitzung(aus(u));
  }), [auth]);
  return <Kontext.Provider value={sitzung}>{children}</Kontext.Provider>;
}

export function useSitzung(): Sitzung {
  return useContext(Kontext);
}

/**
 * Meldet die App mit dem Custom Token des API-Dienstes an (Mandant ist an `auth.tenantId` gesetzt). Bricht die Verbindung
 * dabei ab (Netzwechsel im Wald, `auth/network-request-failed`), versucht die App es mit demselben Token noch zweimal;
 * die Zeremonie selbst ist dann schon abgeschlossen und ließe sich nicht wiederholen.
 */
export async function mitCustomTokenAnmelden(customToken: string): Promise<string> {
  const { auth } = firebaseDienste();
  ausstehendSetzen(customToken);
  for (let versuch = 1; ; versuch++) {
    try {
      const r = await signInWithCustomToken(auth, customToken);
      ausstehendSetzen(null);
      return r.user.uid;
    } catch (e) {
      const code = (e as { code?: string }).code;
      if (code !== "auth/network-request-failed") ausstehendSetzen(null);
      if (versuch >= 3 || code !== "auth/network-request-failed") throw e;
      await new Promise((weiter) => setTimeout(weiter, 500 * versuch));
    }
  }
}

/**
 * Reviere, in denen das Konto Jagdherr ist (Kennung und Name). Der Abschluss einer Passkey-Zeremonie meldet sie
 * (Feld `Reviere`), danach hält die Liste `personen/{uid}/reviere` sie aktuell. Das Gerät merkt sie sich je Konto, damit
 * „Reviere“, die Reviere und ihre Stände sofort und auch ohne Netz erreichbar sind (Stand vor Ort anlegen, revier/05).
 */
export interface GemerktesRevier { Revier: string; Reviername: string }

const reviereSchluessel = (uid: string) => `dj-reviere-${uid}`;

export function reviereMerken(uid: string, reviere: readonly GemerktesRevier[]): void {
  try {
    if (reviere.length) localStorage.setItem(reviereSchluessel(uid), JSON.stringify(reviere));
    else localStorage.removeItem(reviereSchluessel(uid));
  } catch { /* ohne Speicher gilt nur die gelesene Liste */ }
}

export function gemerkteReviere(uid: string | null): GemerktesRevier[] {
  try {
    const roh = uid ? localStorage.getItem(reviereSchluessel(uid)) : null;
    const liste = roh ? (JSON.parse(roh) as unknown) : [];
    return Array.isArray(liste) ? liste.filter((r): r is GemerktesRevier => typeof r?.Revier === "string" && typeof r?.Reviername === "string") : [];
  } catch { return []; }
}

export async function abmelden(): Promise<void> {
  abmeldungSetzen(true);
  ausstehendSetzen(null);
  const uid = firebaseDienste().auth.currentUser?.uid;
  if (uid) reviereMerken(uid, []);
  await signOut(firebaseDienste().auth);
  abmeldungSetzen(false);
}

/** ID-Token für `Authorization: Bearer` (befehle.md §2). */
export async function idToken(): Promise<string | null> {
  const u = firebaseDienste().auth.currentUser;
  return u ? u.getIdToken() : null;
}
